import { useEffect, useRef } from 'react';
import { useSearchParams } from 'react-router-dom';
import { useTestHistory } from '../hooks/useTestHistory';
import { useOffsetSortPagination } from '../hooks/useOffsetSortPagination';
import Card from '../components/Card';
import Table from '../components/Table';
import Pagination from '../components/Pagination';
import Skeleton from '../components/Skeleton';
import { Link } from 'react-router-dom';
import type { Test } from '../lib/types';
import {
  applyListTableToSearchParams,
  initialSortingFromListUrl,
  readListTableSearch,
  searchParamsEffectivelyEqual,
  serverPaginationInitFromListUrl,
} from '../lib/listUrlParams';

function formatDate(dateString?: string): string {
  if (!dateString) return 'N/A';
  try {
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
    });
  } catch {
    return dateString;
  }
}

export default function TestHistoryTab() {
  const [searchParams, setSearchParams] = useSearchParams();
  const urlBootRef = useRef<ReturnType<typeof readListTableSearch> | null>(null);
  if (urlBootRef.current === null) {
    urlBootRef.current = readListTableSearch(new URLSearchParams(window.location.search));
  }
  const urlBoot = urlBootRef.current;
  const {
    pagination,
    sorting,
    listQuery,
    onSortingChange,
    onPageSizeChange,
    syncCursorLastKey,
    canGoNext,
  } = useOffsetSortPagination(20, {
    initialSorting: initialSortingFromListUrl(urlBoot.sortId, urlBoot.orderDesc),
    paginationInit: serverPaginationInitFromListUrl(
      urlBoot.page,
      urlBoot.sortId,
      urlBoot.cursors
    ),
  });

  const { data, isLoading, error } = useTestHistory({
    limit: pagination.pageSize,
    ...listQuery,
  });

  useEffect(() => {
    syncCursorLastKey(data?.lastKey);
  }, [data?.lastKey, syncCursorLastKey]);

  useEffect(() => {
    const next = applyListTableToSearchParams(searchParams, {
      page: pagination.currentPage,
      sorting,
      lastKeys: pagination.lastKeys,
    });
    if (!searchParamsEffectivelyEqual(next, searchParams)) {
      setSearchParams(next, { replace: true });
    }
  }, [
    searchParams,
    setSearchParams,
    pagination.currentPage,
    pagination.lastKeys,
    sorting,
  ]);

  if (isLoading) {
    return (
      <div className="space-y-4">
        <Card className="p-0 overflow-hidden">
          <div className="space-y-4 p-4">
            {[...Array(8)].map((_, i) => (
              <div key={i} className="flex gap-4">
                <Skeleton className="h-10 w-full" />
              </div>
            ))}
          </div>
        </Card>
      </div>
    );
  }

  if (error) {
    return (
      <Card>
        <div className="bg-red-50 border-l-4 border-red-500 rounded-xl p-6">
          <div className="flex items-start gap-3">
            <svg className="w-6 h-6 text-red-600 flex-shrink-0 mt-0.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
            </svg>
            <div>
              <h4 className="text-sm font-semibold text-red-900 mb-1">Error Loading Data</h4>
              <p className="text-sm text-red-700">
                {error instanceof Error ? error.message : 'Failed to load test history.'}
              </p>
            </div>
          </div>
        </div>
      </Card>
    );
  }

  const tests = data?.items || [];
  const totalPages = data?.total ? Math.ceil(data.total / pagination.pageSize) : null;

  const columns = [
    {
      header: 'Pack ID',
      accessor: 'pack_id' as keyof Test,
      mobileLabel: 'Pack ID',
      enableSorting: true,
      cellRenderer: (test: Test) => (
        <Link
          to={`/packs/${test.pack_id}`}
          className="text-blue-600 hover:text-blue-800 hover:underline font-medium"
          onClick={(e) => e.stopPropagation()}
        >
          {test.pack_id}
        </Link>
      ),
    },
    {
      header: 'Test ID',
      accessor: 'test_id' as keyof Test,
      mobileLabel: 'Test ID',
      enableSorting: true,
    },
    {
      header: 'Test Date',
      accessor: 'test_date' as keyof Test,
      mobileLabel: 'Test Date',
      enableSorting: true,
      cellRenderer: (test: Test) => formatDate(test.test_date),
    },
    {
      header: 'Operator',
      accessor: 'test_operator' as keyof Test,
      mobileLabel: 'Operator',
      enableSorting: true,
    },
    {
      header: 'C-Capacity (Ah)',
      accessor: 'c_capacity' as keyof Test,
      mobileLabel: 'C-Capacity (Ah)',
      enableSorting: true,
      cellRenderer: (test: Test) => test.c_capacity || '-',
    },
    {
      header: 'D-Capacity (Ah)',
      accessor: 'd_capacity' as keyof Test,
      mobileLabel: 'D-Capacity (Ah)',
      enableSorting: true,
      cellRenderer: (test: Test) => test.d_capacity || '-',
    },
    {
      header: 'Cycle',
      accessor: 'cycle' as keyof Test,
      mobileLabel: 'Cycle',
      enableSorting: true,
    },
    {
      header: 'Pack Type',
      accessor: 'pack_type' as keyof Test,
      mobileLabel: 'Pack Type',
      enableSorting: true,
    },
  ];

  const handlePageChange = (direction: 'next' | 'prev' | 'first' | 'last') => {
    if (direction === 'next') {
      pagination.nextPage();
    } else if (direction === 'prev') {
      pagination.previousPage();
    } else if (direction === 'first') {
      pagination.reset();
    }
  };

  return (
    <div className="space-y-6">
      <Card>
        <Table
          columns={columns}
          data={tests}
          onRowClick={(test) => window.location.href = `/packs/${test.pack_id}`}
          emptyMessage="No tests found"
          enableSorting={true}
          enablePagination={false}
          total={data?.total ?? null}
          sorting={sorting}
          onSortingChange={onSortingChange}
        />

        {tests.length > 0 && (
          <Pagination
            currentPage={pagination.currentPage}
            totalPages={totalPages}
            total={data?.total ?? null}
            pageSize={pagination.pageSize}
            canGoNext={canGoNext(data?.total ?? 0, data?.lastKey)}
            canGoPrevious={pagination.canGoPrevious}
            onPageChange={handlePageChange}
            onPageSizeChange={onPageSizeChange}
          />
        )}
      </Card>
    </div>
  );
}
